import { z } from "zod";
import { postingKeyNameSchema } from "../accounting/posting-keys.js";
import { businessUnitTypeSchema } from "../auth/features.js";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, nonNegativeMoneySchema, quantitySchema, rateSchema, timestampSchema, unitPriceSchema, uuidSchema, } from "../common/primitives.js";
const optionalText = (max) => z.string().trim().max(max).nullable().default(null);
const positiveMoneySchema = moneySchema.refine((value) => BigInt(value) > 0n, "Tutar sıfırdan büyük olmalıdır");
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
const reasonSchema = z.string().trim().min(3, "Gerekçe en az 3 karakter olmalıdır").max(500);
const taxIdSchema = z
    .string()
    .trim()
    .regex(/^\d{10,11}$/, "VKN 10, TCKN 11 haneli olmalıdır");
const timeSchema = z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, "Saat SS:dd biçiminde olmalıdır")
    .describe("Yerel saat (Europe/Istanbul), SS:dd");
// ── Sabitler ve etiketler ────────────────────────────────────────────────────
export const INVOICE_DIRECTIONS = ["SALES", "PURCHASE"];
export const invoiceDirectionSchema = z.enum(INVOICE_DIRECTIONS);
export const INVOICE_DIRECTION_LABELS = {
    SALES: "Satış",
    PURCHASE: "Alış",
};
/** Belgeyi kim düzenledi: OUTGOING = biz, INCOMING = karşı taraf. İadede akış terstir (`invoiceFlowOf`). */
export const INVOICE_FLOWS = ["OUTGOING", "INCOMING"];
export const invoiceFlowSchema = z.enum(INVOICE_FLOWS);
export const INVOICE_FLOW_LABELS = { OUTGOING: "Giden", INCOMING: "Gelen" };
/** Genişletilebilir: yeni tür (OZELMATRAH, IHRACKAYITLI…) eklemek için veritabanı değişikliği gerekmez. */
export const INVOICE_TYPES = ["SATIS", "IADE", "TEVKIFAT", "ISTISNA"];
export const invoiceTypeSchema = z.enum(INVOICE_TYPES);
export const INVOICE_TYPE_LABELS = {
    SATIS: "Satış",
    IADE: "İade",
    TEVKIFAT: "Tevkifatlı",
    ISTISNA: "İstisna",
};
export const INVOICE_SCENARIOS = ["TEMELFATURA", "TICARIFATURA", "EARSIVFATURA", "KAGIT"];
export const invoiceScenarioSchema = z.enum(INVOICE_SCENARIOS);
export const INVOICE_SCENARIO_LABELS = {
    TEMELFATURA: "e-Fatura (temel)",
    TICARIFATURA: "e-Fatura (ticari)",
    EARSIVFATURA: "e-Arşiv",
    KAGIT: "Kâğıt",
};
export const INVOICE_BASES = ["STANDARD", "FISCAL_RECEIPTS"];
export const invoiceBasisSchema = z.enum(INVOICE_BASES);
export const INVOICE_BASIS_LABELS = {
    STANDARD: "Normal",
    FISCAL_RECEIPTS: "Fişli satışların faturası",
};
export const INVOICE_STATUSES = ["DRAFT", "ISSUED", "CANCELLED"];
export const invoiceStatusSchema = z.enum(INVOICE_STATUSES);
export const INVOICE_STATUS_LABELS = {
    DRAFT: "Taslak",
    ISSUED: "Kesinleşti",
    CANCELLED: "İptal edildi",
};
export const EDOCUMENT_STATUSES = [
    "NOT_APPLICABLE",
    "QUEUED",
    "SENDING",
    "SENT",
    "DELIVERED",
    "ACCEPTED",
    "REJECTED",
    "REPORTED",
    "ERROR",
    "CANCELLED",
];
export const edocumentStatusSchema = z.enum(EDOCUMENT_STATUSES);
export const EDOCUMENT_STATUS_LABELS = {
    NOT_APPLICABLE: "e-Belge değil",
    QUEUED: "Kuyrukta",
    SENDING: "Gönderiliyor",
    SENT: "Gönderildi",
    DELIVERED: "Alıcıya ulaştı",
    ACCEPTED: "Kabul edildi",
    REJECTED: "Reddedildi",
    REPORTED: "GİB'e raporlandı",
    ERROR: "Hata",
    CANCELLED: "İptal edildi",
};
/** Sağlayıcıda süreci bitmemiş durumlar: periyodik durum sorgusu bunları izler. */
export const EDOCUMENT_PENDING_STATUSES = [
    "QUEUED",
    "SENDING",
    "SENT",
    "DELIVERED",
];
export const PRICING_MODES = ["VAT_INCLUSIVE", "VAT_EXCLUSIVE"];
export const pricingModeSchema = z.enum(PRICING_MODES);
export const PRICING_MODE_LABELS = {
    VAT_INCLUSIVE: "KDV dâhil",
    VAT_EXCLUSIVE: "KDV hariç",
};
export const INVOICE_SETTLEMENTS = ["ON_ACCOUNT", "CASH", "POS"];
export const invoiceSettlementSchema = z.enum(INVOICE_SETTLEMENTS);
export const INVOICE_SETTLEMENT_LABELS = {
    ON_ACCOUNT: "Cariye (vadeli)",
    CASH: "Peşin — kasa",
    POS: "Peşin — POS",
};
export const INVOICE_SERIES_FAMILIES = ["E_INVOICE", "E_ARCHIVE", "E_DESPATCH", "PAPER"];
export const invoiceSeriesFamilySchema = z.enum(INVOICE_SERIES_FAMILIES);
export const INVOICE_SERIES_FAMILY_LABELS = {
    E_INVOICE: "e-Fatura",
    E_ARCHIVE: "e-Arşiv",
    E_DESPATCH: "e-İrsaliye",
    PAPER: "Kâğıt",
};
/** UN/ECE Rec 20 birim kodları (UBL-TR). Ürün birimi → kod eşlemesi `UNIT_CODE_BY_PRODUCT_UNIT`. */
export const INVOICE_UNIT_CODES = [
    "C62",
    "LTR",
    "KGM",
    "KWH",
    "MIN",
    "HUR",
    "DAY",
    "MON",
    "MTR",
    "MTK",
    "PA",
];
export const invoiceUnitCodeSchema = z.enum(INVOICE_UNIT_CODES);
export const INVOICE_UNIT_CODE_LABELS = {
    C62: "Adet",
    LTR: "Litre",
    KGM: "Kilogram",
    KWH: "kWh",
    MIN: "Dakika",
    HUR: "Saat",
    DAY: "Gün",
    MON: "Ay",
    MTR: "Metre",
    MTK: "Metrekare",
    PA: "Paket",
};
export const UNIT_CODE_BY_PRODUCT_UNIT = {
    LITRE: "LTR",
    KG: "KGM",
    PIECE: "C62",
    KWH: "KWH",
    MINUTE: "MIN",
};
/** Fatura satırının muhasebe hedefi olabilecek anahtarlar. */
export const SALES_LINE_POSTING_KEYS = ["SALES_REVENUE", "OTHER_INCOME"];
export const PURCHASE_LINE_POSTING_KEYS = ["INVENTORY", "EXPENSE"];
/** İadede belge akışı terstir: müşterinin iadesini müşteri, tedarikçiye iadeyi biz düzenleriz. */
export function invoiceFlowOf(direction, invoiceType) {
    return (direction === "SALES") !== (invoiceType === "IADE") ? "OUTGOING" : "INCOMING";
}
/** Fatura numarası: seri (3) + yıl (4) + sıra (9) = 16 hane. */
export function formatInvoiceNo(prefix, year, sequence) {
    return `${prefix}${String(year).padStart(4, "0")}${String(sequence).padStart(9, "0")}`;
}
// ── Ayarlar, seriler, sağlayıcı ──────────────────────────────────────────────
export const invoicingSettingsSchema = z.object({
    companyId: uuidSchema,
    defaultPricingMode: pricingModeSchema,
    defaultEinvoiceScenario: z.enum(["TEMELFATURA", "TICARIFATURA"]),
    defaultDueDays: z.number().int().min(0).max(365),
    responseDeadlineDays: z
        .number()
        .int()
        .min(1)
        .max(60)
        .describe("Ticari faturada kabul / red süresi (gün) — mevzuat parametresi, doğrulanmalı"),
    responseAlertDays: z.number().int().min(0).max(30).describe("Son güne kaç gün kala alarm üretilir"),
    taxpayerCacheDays: z.number().int().min(0).max(90),
    autoSend: z.boolean().describe("Kesinleşen e-belge sağlayıcı kuyruğuna otomatik alınır"),
    incomingFetchEnabled: z.boolean(),
    version: versionSchema,
});
export const updateInvoicingSettingsSchema = patchOf(invoicingSettingsSchema.omit({ companyId: true, version: true })).extend({ version: versionSchema });
export const invoiceSeriesSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    prefix: z.string().describe("3 karakter; belge no: ABC2026000000001"),
    family: invoiceSeriesFamilySchema,
    description: z.string().nullable(),
    isDefault: z.boolean(),
    isActive: z.boolean(),
    lastNumbers: z
        .array(z.object({ year: z.number().int(), lastValue: z.string() }))
        .describe("Yıl bazında verilen son sıra numarası"),
    version: versionSchema,
});
export const invoiceSeriesListSchema = z.object({ items: z.array(invoiceSeriesSchema) });
export const listInvoiceSeriesQuerySchema = z.object({
    companyId: uuidSchema,
    family: invoiceSeriesFamilySchema.optional(),
});
export const createInvoiceSeriesSchema = z.object({
    companyId: uuidSchema,
    prefix: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9]{3}$/, "Seri öneki 3 karakter olmalıdır (A–Z, 0–9)"),
    family: invoiceSeriesFamilySchema,
    description: optionalText(200),
    isDefault: z.boolean().default(false),
});
export const updateInvoiceSeriesSchema = z.object({
    description: z.string().trim().max(200).nullable().optional(),
    isDefault: z.boolean().optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
export const EDOCUMENT_PROVIDER_CODES = ["MOCK"];
export const edocumentProviderCodeSchema = z.enum(EDOCUMENT_PROVIDER_CODES);
export const EDOCUMENT_PROVIDER_LABELS = {
    MOCK: "Deneme sağlayıcısı (sahte)",
};
/** Sahte sağlayıcının davranış ayarları (gizli değildir). */
export const mockProviderOptionsSchema = z.object({
    stepSeconds: z.number().int().min(0).max(3600).default(5).describe("Durumlar arası gecikme (sn)"),
    failureRate: z.number().int().min(0).max(100).default(0).describe("Geçici hata olasılığı (%)"),
    rejectTaxIds: z.array(taxIdSchema).max(50).default([]).describe("Bu alıcılar ticari faturayı REDDEDER"),
    einvoiceTaxIds: z
        .array(taxIdSchema)
        .max(200)
        .default([])
        .describe("e-Fatura mükellefi sayılacak VKN'ler; boşsa 10 haneli VKN'si çift rakamla biten herkes mükelleftir"),
    failFirstAttempts: z
        .number()
        .int()
        .min(0)
        .max(20)
        .default(0)
        .describe("Her belgenin ilk N gönderim denemesi geçici hatayla sonuçlanır (yeniden deneme testi)"),
    permanentFailTaxIds: z
        .array(taxIdSchema)
        .max(50)
        .default([])
        .describe("Bu alıcılara gönderim KALICI hata verir"),
    generateIncoming: z.boolean().default(true).describe("Gelen fatura üretir (demo)"),
    incomingSenders: z
        .array(z.object({ taxId: taxIdSchema, title: z.string().trim().min(1).max(300) }))
        .max(20)
        .default([])
        .describe("Gelen faturaların göndericileri; boşsa yerleşik örnek tedarikçiler"),
    incomingPerDay: z.number().int().min(0).max(10).default(1),
});
export const edocumentProviderConfigSchema = z.object({
    companyId: uuidSchema,
    providerCode: edocumentProviderCodeSchema,
    isActive: z.boolean(),
    hasCredentials: z.boolean().describe("Kimlik bilgisi kayıtlı mı (değerin kendisi ASLA dönmez)"),
    options: z.record(z.string(), z.unknown()),
    einvoiceAlias: z.string().nullable().describe("Gönderici birim etiketi (GB)"),
    edespatchAlias: z.string().nullable(),
    version: versionSchema,
});
/** PUT: kayıt yoksa oluşturur, varsa TÜMÜNÜ değiştirir (kimlik bilgisi hariç — aşağıya bakın). */
export const saveEdocumentProviderConfigSchema = z.object({
    providerCode: edocumentProviderCodeSchema,
    isActive: z.boolean().default(true),
    credentials: z
        .object({
        username: z.string().trim().min(1).max(200),
        password: z.string().min(1).max(500),
        apiKey: z.string().max(500).nullable().default(null),
    })
        .nullable()
        .optional()
        .describe("Yazma-yalnız; verilmezse mevcut değer korunur, null verilirse silinir. Şifreli saklanır."),
    options: z.record(z.string(), z.unknown()).default({}),
    einvoiceAlias: optionalText(200),
    edespatchAlias: optionalText(200),
    version: versionSchema.optional(),
});
export const taxpayerLookupRequestSchema = z.object({
    companyId: uuidSchema,
    taxId: taxIdSchema,
    refresh: z.boolean().default(false).describe("Önbelleği atla"),
});
export const taxpayerLookupSchema = z.object({
    taxId: z.string(),
    isEinvoiceUser: z.boolean(),
    isEdespatchUser: z.boolean(),
    title: z.string().nullable(),
    einvoiceAliases: z.array(z.string()),
    edespatchAliases: z.array(z.string()),
    checkedAt: timestampSchema,
    fromCache: z.boolean(),
});
// ── Fatura: girdi ────────────────────────────────────────────────────────────
export const invoiceDiscountSchema = z
    .object({
    kind: z.enum(["RATE", "AMOUNT"]),
    value: z.string().regex(/^\d{1,14}(\.\d{1,4})?$/, "İskonto sayısal olmalıdır"),
})
    .describe('RATE: yüzde (0–100). AMOUNT: kuruş DEĞİL, TL tutarı (ör. "12.50"), belgenin giriş modu esasında');
export const invoiceLineInputSchema = z.object({
    productId: uuidSchema.nullable().default(null),
    description: z.string().trim().min(1, "Açıklama zorunludur").max(300),
    quantity: quantitySchema,
    unitCode: invoiceUnitCodeSchema,
    unitPrice: unitPriceSchema,
    discount: invoiceDiscountSchema.nullable().default(null),
    vatRateCode: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{1,39}$/)
        .describe("tax_rates.code; oran belge tarihine göre çözülür ve satırda dondurulur"),
    withholdingCode: z.string().trim().max(20).nullable().default(null),
    exemptionCode: z.string().trim().max(20).nullable().default(null),
    postingKey: postingKeyNameSchema
        .nullable()
        .default(null)
        .describe("Boşsa satışta SALES_REVENUE (iadede SALES_RETURNS); alışta ZORUNLU: INVENTORY ya da EXPENSE"),
    postingParam: z
        .string()
        .trim()
        .max(60)
        .nullable()
        .default(null)
        .describe("Muhasebe grubu / gider kategorisi"),
    businessUnitType: businessUnitTypeSchema.nullable().default(null),
    originalLineId: uuidSchema.nullable().default(null).describe("İade satırının bağlı olduğu fatura satırı"),
});
export const fiscalReceiptRefSchema = z.object({
    fiscalDeviceSerial: z.string().trim().min(1).max(40).describe("ÖKC sicil / seri no"),
    zNo: z.number().int().positive(),
    receiptNo: z.number().int().positive(),
    receiptDate: localDateSchema,
    receiptTime: timeSchema.nullable().default(null),
    amount: positiveMoneySchema.describe("KDV dâhil fiş tutarı (kuruş)"),
    stationId: uuidSchema.nullable().default(null),
    saleRef: z.string().trim().max(100).nullable().default(null),
});
const invoiceFields = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.nullable().default(null),
    direction: invoiceDirectionSchema,
    invoiceType: invoiceTypeSchema.default("SATIS"),
    basis: invoiceBasisSchema.default("STANDARD"),
    counterpartyId: uuidSchema,
    seriesId: uuidSchema.nullable().default(null).describe("Giden belgede boşsa ailenin varsayılan serisi"),
    scenario: invoiceScenarioSchema
        .nullable()
        .default(null)
        .describe("Giden belgede boşsa mükellef sorgusuna göre seçilir; gelen belgede zorunlu"),
    invoiceNo: z
        .string()
        .trim()
        .min(1)
        .max(40)
        .nullable()
        .default(null)
        .describe("YALNIZCA gelen belgede (karşı tarafın numarası); giden belgede kesinleştirmede üretilir"),
    ettn: uuidSchema.nullable().default(null).describe("Gelen e-belgenin ETTN'si; giden belgede üretilir"),
    issueDate: localDateSchema,
    issueTime: timeSchema.default("00:00"),
    dueDate: localDateSchema.nullable().default(null).describe("Boşsa belge tarihi + varsayılan vade günü"),
    pricingMode: pricingModeSchema.nullable().default(null).describe("Boşsa şirket ayarındaki varsayılan"),
    documentDiscount: invoiceDiscountSchema.nullable().default(null),
    settlement: invoiceSettlementSchema.default("ON_ACCOUNT"),
    cashRegisterId: uuidSchema.nullable().default(null),
    posTerminalId: uuidSchema.nullable().default(null),
    notes: optionalText(2000),
    originalInvoiceId: uuidSchema.nullable().default(null),
    deliveryId: uuidSchema.nullable().default(null).describe("Tanker dolum kaydı (eşleştirme Prompt 12'de)"),
    despatchIds: z.array(uuidSchema).max(50).default([]),
    earchiveRequestId: uuidSchema.nullable().default(null),
    receipts: z
        .array(fiscalReceiptRefSchema)
        .max(2000)
        .default([])
        .describe("basis = FISCAL_RECEIPTS için zorunlu"),
    lines: z.array(invoiceLineInputSchema).min(1, "En az bir satır gereklidir").max(500),
});
function invoiceRules(value, context) {
    const issue = (path, message) => context.addIssue({ code: "custom", path, message });
    if (value.direction && value.invoiceType) {
        const flow = invoiceFlowOf(value.direction, value.invoiceType);
        if (flow === "INCOMING") {
            if (value.invoiceNo === null)
                issue(["invoiceNo"], "Karşı tarafın düzenlediği belgede fatura numarası zorunludur");
            if (value.scenario === null)
                issue(["scenario"], "Gelen belgede senaryo (e-Fatura / e-Arşiv / kâğıt) seçilmelidir");
            if (value.seriesId)
                issue(["seriesId"], "Gelen belgede seri seçilemez");
        }
        else {
            if (value.invoiceNo)
                issue(["invoiceNo"], "Giden belgenin numarası kesinleştirmede üretilir");
            if (value.ettn)
                issue(["ettn"], "Giden belgenin ETTN'si sistemce üretilir");
        }
        if (value.settlement &&
            value.settlement !== "ON_ACCOUNT" &&
            !(value.direction === "SALES" && flow === "OUTGOING")) {
            issue(["settlement"], "Peşin tahsilat yalnızca satış faturasında seçilebilir");
        }
    }
    if (value.settlement === "CASH" && !value.cashRegisterId)
        issue(["cashRegisterId"], "Kasa seçilmelidir");
    if (value.settlement === "POS" && !value.posTerminalId)
        issue(["posTerminalId"], "POS terminali seçilmelidir");
    if (value.settlement !== "CASH" && value.cashRegisterId)
        issue(["cashRegisterId"], "Kasa yalnızca peşin (kasa) tahsilatta seçilir");
    if (value.settlement !== "POS" && value.posTerminalId)
        issue(["posTerminalId"], "POS yalnızca peşin (POS) tahsilatta seçilir");
    if (value.dueDate && value.issueDate && value.dueDate < value.issueDate) {
        issue(["dueDate"], "Vade, belge tarihinden önce olamaz");
    }
    if (value.basis === "FISCAL_RECEIPTS") {
        if (value.direction !== "SALES" || value.invoiceType === "IADE") {
            issue(["basis"], "Fişli satış faturası yalnızca satış faturası olabilir");
        }
        if (value.settlement && value.settlement !== "ON_ACCOUNT") {
            issue(["settlement"], "Fişli satışın tahsilatı satış anında kaydedilmiştir; burada seçilemez");
        }
        if ((value.receipts ?? []).length === 0)
            issue(["receipts"], "En az bir ÖKC fişi seçilmelidir");
        const seen = new Set();
        (value.receipts ?? []).forEach((receipt, index) => {
            const key = `${receipt.fiscalDeviceSerial}|${receipt.zNo}|${receipt.receiptNo}`;
            if (seen.has(key))
                issue(["receipts", index, "receiptNo"], "Aynı fiş iki kez eklenmiş");
            seen.add(key);
        });
    }
    else if ((value.receipts ?? []).length > 0) {
        issue(["receipts"], "ÖKC fişi yalnızca fişli satış faturasına bağlanır");
    }
    (value.lines ?? []).forEach((line, index) => {
        if (line.exemptionCode && line.withholdingCode) {
            issue(["lines", index, "withholdingCode"], "İstisna satırına tevkifat uygulanamaz");
        }
        if (value.invoiceType === "ISTISNA" && !line.exemptionCode) {
            issue(["lines", index, "exemptionCode"], "İstisna faturasında her satırda istisna kodu zorunludur");
        }
        if (value.invoiceType === "SATIS" && line.withholdingCode) {
            issue(["lines", index, "withholdingCode"], 'Tevkifat için fatura türü "Tevkifatlı" seçilmelidir');
        }
        if (value.invoiceType !== "IADE" && line.originalLineId) {
            issue(["lines", index, "originalLineId"], "Asıl satır bağı yalnızca iade faturasında kullanılır");
        }
        if (value.direction === "PURCHASE" && value.invoiceType !== "IADE" && !line.postingKey) {
            issue(["lines", index, "postingKey"], "Alış satırında hesap hedefi (stok ya da gider) seçilmelidir");
        }
    });
    if (value.invoiceType === "TEVKIFAT" && !(value.lines ?? []).some((line) => line.withholdingCode)) {
        issue(["lines"], "Tevkifatlı faturada en az bir satırda tevkifat kodu olmalıdır");
    }
}
export const createInvoiceSchema = invoiceFields
    .extend({
    issue: z.boolean().default(false).describe("true: taslak oluşturmadan doğrudan kesinleştirir"),
})
    .superRefine(invoiceRules);
/** Taslak güncelleme: satırlar verilirse TAMAMI değiştirilir. Şirket, yön ve tür değişmez. */
export const updateInvoiceSchema = patchOf(invoiceFields.omit({ companyId: true, direction: true, invoiceType: true, basis: true })).extend({ version: versionSchema });
export const previewInvoiceSchema = invoiceFields.superRefine(invoiceRules);
export const issueInvoiceSchema = z.object({ version: versionSchema });
export const cancelInvoiceSchema = z.object({ reason: reasonSchema, version: versionSchema });
export const sendInvoiceSchema = z.object({ version: versionSchema });
export const setInvoiceDespatchesSchema = z.object({ despatchIds: z.array(uuidSchema).max(50) });
// ── Fatura: çıktı ────────────────────────────────────────────────────────────
export const invoiceLineSchema = z.object({
    id: uuidSchema,
    lineNo: z.number().int(),
    productId: uuidSchema.nullable(),
    productCode: z.string().nullable(),
    description: z.string(),
    quantity: z.string(),
    unitCode: z.string(),
    unitPrice: z.string(),
    discount: invoiceDiscountSchema.nullable(),
    vatRateCode: z.string(),
    vatRate: rateSchema,
    withholdingCode: z.string().nullable(),
    withholdingNumerator: z.number().int().nullable(),
    withholdingDenominator: z.number().int().nullable(),
    exemptionCode: z.string().nullable(),
    postingKey: z.string(),
    postingParam: z.string().nullable(),
    businessUnitType: businessUnitTypeSchema.nullable(),
    amount: nonNegativeMoneySchema.describe("miktar × birim fiyat (iskonto öncesi, giriş modu esasında)"),
    lineDiscountAmount: nonNegativeMoneySchema,
    docDiscountShare: nonNegativeMoneySchema,
    grossNetAmount: nonNegativeMoneySchema,
    discountNetAmount: nonNegativeMoneySchema,
    netAmount: nonNegativeMoneySchema.describe("Matrah"),
    vatAmount: nonNegativeMoneySchema,
    grossAmount: nonNegativeMoneySchema.describe("KDV dâhil"),
    withheldAmount: nonNegativeMoneySchema,
    originalLineId: uuidSchema.nullable(),
    returnedQuantity: z
        .string()
        .describe("Bu satırdan bugüne dek iade edilen miktar (iptal edilmemiş iadeler)"),
});
export const invoiceTotalsSchema = z.object({
    grossNetAmount: nonNegativeMoneySchema.describe("İskonto öncesi KDV hariç toplam"),
    discountNetAmount: nonNegativeMoneySchema,
    netAmount: nonNegativeMoneySchema.describe("Toplam matrah"),
    vatAmount: nonNegativeMoneySchema,
    grossAmount: nonNegativeMoneySchema.describe("KDV dâhil toplam"),
    withheldAmount: nonNegativeMoneySchema.describe("Tevkifat"),
    payableAmount: nonNegativeMoneySchema.describe("Ödenecek tutar"),
});
export const invoiceTaxTotalSchema = z.object({
    vatRate: rateSchema,
    netAmount: nonNegativeMoneySchema,
    vatAmount: nonNegativeMoneySchema,
});
export const invoiceWithholdingTotalSchema = z.object({
    code: z.string(),
    name: z.string().nullable(),
    numerator: z.number().int(),
    denominator: z.number().int(),
    vatAmount: nonNegativeMoneySchema,
    withheldAmount: nonNegativeMoneySchema,
});
export const invoiceSummarySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    direction: invoiceDirectionSchema,
    flow: invoiceFlowSchema,
    invoiceType: invoiceTypeSchema,
    scenario: invoiceScenarioSchema,
    basis: invoiceBasisSchema,
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    invoiceNo: z.string().nullable(),
    issueDate: localDateSchema,
    dueDate: localDateSchema,
    netAmount: nonNegativeMoneySchema,
    vatAmount: nonNegativeMoneySchema,
    payableAmount: nonNegativeMoneySchema,
    status: invoiceStatusSchema,
    edocumentStatus: edocumentStatusSchema,
    edocumentError: z.string().nullable(),
    ledgerStatus: z.enum(["NONE", "POSTED", "QUEUED", "REVERSED"]),
    version: versionSchema,
});
export const invoicePageSchema = pageOf(invoiceSummarySchema);
export const invoiceReceiptRefSchema = fiscalReceiptRefSchema
    .omit({ amount: true })
    .extend({ id: uuidSchema, amount: moneySchema, isVoid: z.boolean() });
export const invoiceSchema = invoiceSummarySchema.extend({
    seriesId: uuidSchema.nullable(),
    ettn: uuidSchema,
    issueTime: z.string(),
    pricingMode: pricingModeSchema,
    documentDiscount: invoiceDiscountSchema.nullable(),
    settlement: invoiceSettlementSchema,
    cashRegisterId: uuidSchema.nullable(),
    posTerminalId: uuidSchema.nullable(),
    notes: z.string().nullable(),
    totals: invoiceTotalsSchema,
    taxTotals: z.array(invoiceTaxTotalSchema),
    withholdingTotals: z.array(invoiceWithholdingTotalSchema),
    lines: z.array(invoiceLineSchema),
    receipts: z.array(invoiceReceiptRefSchema),
    originalInvoiceId: uuidSchema.nullable(),
    originalInvoiceNo: z.string().nullable(),
    returnInvoices: z.array(z.object({ id: uuidSchema, invoiceNo: z.string().nullable(), status: invoiceStatusSchema })),
    incomingInvoiceId: uuidSchema.nullable(),
    deliveryId: uuidSchema.nullable(),
    earchiveRequestId: uuidSchema.nullable(),
    despatches: z.array(z.object({ id: uuidSchema, despatchNo: z.string().nullable(), issueDate: localDateSchema })),
    providerCode: z.string().nullable(),
    providerRef: z.string().nullable(),
    receiverAlias: z.string().nullable(),
    sendAttempts: z.number().int(),
    journalEntries: z.array(z.object({ id: uuidSchema, entryNo: z.string().nullable(), purpose: z.string(), status: z.string() })),
    issuedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdAt: timestampSchema,
});
export const invoicePreviewSchema = z.object({
    flow: invoiceFlowSchema,
    scenario: invoiceScenarioSchema
        .nullable()
        .describe("Giden belgede mükellef sorgusuna göre önerilen senaryo"),
    pricingMode: pricingModeSchema,
    dueDate: localDateSchema,
    totals: invoiceTotalsSchema,
    taxTotals: z.array(invoiceTaxTotalSchema),
    withholdingTotals: z.array(invoiceWithholdingTotalSchema),
    lines: z.array(z.object({
        lineNo: z.number().int(),
        vatRate: rateSchema,
        amount: nonNegativeMoneySchema,
        netAmount: nonNegativeMoneySchema,
        vatAmount: nonNegativeMoneySchema,
        grossAmount: nonNegativeMoneySchema,
        withheldAmount: nonNegativeMoneySchema,
    })),
    journalLines: z
        .array(z.object({
        key: z.string().describe("Anlamsal kayıt anahtarı (parametreli)"),
        accountCode: z
            .string()
            .nullable()
            .describe("Eşleştirme eksikse null (fiş bekleyenler kuyruğuna düşer)"),
        accountName: z.string().nullable(),
        side: z.enum(["DEBIT", "CREDIT"]),
        amount: moneySchema,
    }))
        .describe("Kesinleştirmede kesilecek fişin önizlemesi; fişli satış faturasında BOŞTUR"),
    warnings: z.array(z.string()),
});
export const listInvoicesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    direction: invoiceDirectionSchema.optional(),
    flow: invoiceFlowSchema.optional(),
    status: invoiceStatusSchema.optional(),
    edocumentStatus: edocumentStatusSchema.optional(),
    invoiceType: invoiceTypeSchema.optional(),
    basis: invoiceBasisSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    search: z.string().trim().max(100).optional().describe("Fatura no ya da cari adı"),
});
export const edocumentSubmissionSchema = z.object({
    id: uuidSchema,
    operation: z.string(),
    attempt: z.number().int(),
    providerCode: z.string(),
    outcome: z.enum(["SUCCESS", "RETRYABLE_ERROR", "PERMANENT_ERROR"]),
    providerRef: z.string().nullable(),
    resultingStatus: z.string().nullable(),
    errorCode: z.string().nullable(),
    errorMessage: z.string().nullable(),
    durationMs: z.number().int(),
    createdAt: timestampSchema,
});
export const edocumentSubmissionListSchema = z.object({ items: z.array(edocumentSubmissionSchema) });
// ── Gelen faturalar kutusu ───────────────────────────────────────────────────
export const INCOMING_RESPONSE_STATUSES = [
    "NOT_REQUIRED",
    "PENDING",
    "ACCEPTED",
    "REJECTED",
    "EXPIRED",
];
export const incomingResponseStatusSchema = z.enum(INCOMING_RESPONSE_STATUSES);
export const INCOMING_RESPONSE_STATUS_LABELS = {
    NOT_REQUIRED: "Yanıt gerekmez",
    PENDING: "Yanıt bekliyor",
    ACCEPTED: "Kabul edildi",
    REJECTED: "Reddedildi",
    EXPIRED: "Süre doldu (kabul sayıldı)",
};
export const INCOMING_BOOKING_STATUSES = ["PENDING", "BOOKED", "IGNORED"];
export const incomingBookingStatusSchema = z.enum(INCOMING_BOOKING_STATUSES);
export const INCOMING_BOOKING_STATUS_LABELS = {
    PENDING: "Deftere alınmadı",
    BOOKED: "Deftere alındı",
    IGNORED: "Yok sayıldı",
};
export const incomingInvoiceLineSchema = z.object({
    lineNo: z.number().int(),
    sellerItemCode: z.string().nullable(),
    description: z.string(),
    quantity: z.string(),
    unitCode: z.string(),
    unitPrice: z.string(),
    discountAmount: nonNegativeMoneySchema,
    vatRate: rateSchema,
    withholdingCode: z.string().nullable(),
    exemptionCode: z.string().nullable(),
    netAmount: nonNegativeMoneySchema,
    vatAmount: nonNegativeMoneySchema,
});
export const incomingInvoiceSummarySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    ettn: uuidSchema,
    senderTaxId: z.string(),
    senderTitle: z.string(),
    invoiceNo: z.string(),
    scenario: z.enum(["TEMELFATURA", "TICARIFATURA", "EARSIVFATURA"]),
    invoiceType: z.string(),
    issueDate: localDateSchema,
    netAmount: nonNegativeMoneySchema,
    vatAmount: nonNegativeMoneySchema,
    withheldAmount: nonNegativeMoneySchema,
    payableAmount: nonNegativeMoneySchema,
    counterpartyId: uuidSchema.nullable(),
    counterpartyName: z.string().nullable(),
    receivedAt: timestampSchema,
    responseStatus: incomingResponseStatusSchema,
    responseDueDate: localDateSchema.nullable(),
    daysLeft: z.number().int().nullable().describe("Kabul / red için kalan gün (yalnızca yanıt bekleyenlerde)"),
    bookingStatus: incomingBookingStatusSchema,
    bookedInvoiceId: uuidSchema.nullable(),
    version: versionSchema,
});
export const incomingInvoicePageSchema = pageOf(incomingInvoiceSummarySchema);
export const incomingInvoiceSchema = incomingInvoiceSummarySchema.extend({
    issueTime: z.string(),
    currency: z.string(),
    despatchRefs: z.array(z.string()),
    notes: z.string().nullable(),
    ublFileId: uuidSchema.nullable(),
    rejectReason: z.string().nullable(),
    ignoreReason: z.string().nullable(),
    respondedAt: timestampSchema.nullable(),
    lines: z.array(incomingInvoiceLineSchema),
    counterpartySuggestion: z
        .object({ name: z.string(), taxId: z.string(), taxIdKind: z.enum(["VKN", "TCKN"]) })
        .nullable()
        .describe("VKN ile cari bulunamadıysa açılması önerilen tedarikçi kartı"),
});
export const listIncomingInvoicesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    responseStatus: incomingResponseStatusSchema.optional(),
    bookingStatus: incomingBookingStatusSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    search: z.string().trim().max(100).optional(),
});
export const fetchIncomingInvoicesSchema = z.object({ companyId: uuidSchema });
export const fetchIncomingInvoicesResultSchema = z.object({
    fetched: z.number().int(),
    created: z.number().int(),
    matched: z.number().int().describe("VKN ile tedarikçi carisine otomatik eşleşen"),
});
export const respondIncomingInvoiceSchema = z
    .object({
    action: z.enum(["ACCEPT", "REJECT"]),
    reason: reasonSchema.nullable().default(null),
    version: versionSchema,
})
    .superRefine((value, context) => {
    if (value.action === "REJECT" && !value.reason) {
        context.addIssue({ code: "custom", path: ["reason"], message: "Red gerekçesi zorunludur" });
    }
});
export const matchIncomingCounterpartySchema = z.object({
    counterpartyId: uuidSchema.nullable().describe("null: VKN ile yeni tedarikçi kartı açılır ve eşleştirilir"),
    version: versionSchema,
});
export const ignoreIncomingInvoiceSchema = z.object({ reason: reasonSchema, version: versionSchema });
export const bookingLineSuggestionSchema = z.object({
    lineNo: z.number().int(),
    productId: uuidSchema.nullable(),
    productCode: z.string().nullable(),
    postingKey: z.string().nullable(),
    postingParam: z.string().nullable(),
    vatRateCode: z.string().nullable(),
    source: z
        .enum(["LEARNED", "PRODUCT_CODE", "NONE"])
        .describe("LEARNED: aynı tedarikçinin önceki faturasından öğrenildi"),
    useCount: z.number().int(),
});
export const bookingSuggestionSchema = z.object({
    dueDate: localDateSchema,
    lines: z.array(bookingLineSuggestionSchema),
});
export const bookIncomingInvoiceSchema = z.object({
    stationId: uuidSchema.nullable().default(null),
    dueDate: localDateSchema.nullable().default(null),
    deliveryId: uuidSchema.nullable().default(null),
    despatchIds: z.array(uuidSchema).max(50).default([]),
    notes: optionalText(2000),
    lines: z
        .array(z.object({
        lineNo: z.number().int().positive(),
        productId: uuidSchema.nullable().default(null),
        postingKey: z.enum(PURCHASE_LINE_POSTING_KEYS),
        postingParam: z.string().trim().max(60).nullable().default(null),
        vatRateCode: z.string().regex(/^[A-Z][A-Z0-9_]{1,39}$/),
        businessUnitType: businessUnitTypeSchema.nullable().default(null),
    }))
        .min(1),
    learn: z.boolean().default(true).describe("Seçimleri bir sonraki fatura için öneri olarak kaydet"),
    version: versionSchema,
});
// ── İrsaliyeler ──────────────────────────────────────────────────────────────
export const DESPATCH_STATUSES = INVOICE_STATUSES;
export const RECEIPT_ADVICE_STATUSES = ["NONE", "ACCEPTED", "PARTIAL", "REJECTED"];
export const receiptAdviceStatusSchema = z.enum(RECEIPT_ADVICE_STATUSES);
export const RECEIPT_ADVICE_STATUS_LABELS = {
    NONE: "Yanıt yok",
    ACCEPTED: "Teslim alındı",
    PARTIAL: "Kısmen teslim alındı",
    REJECTED: "Reddedildi",
};
export const DESPATCH_FLOW_LABELS = {
    OUTGOING: "Sevk irsaliyesi",
    INCOMING: "Alış irsaliyesi",
};
export const despatchLineInputSchema = z.object({
    productId: uuidSchema.nullable().default(null),
    description: z.string().trim().min(1, "Açıklama zorunludur").max(300),
    quantity: quantitySchema,
    unitCode: invoiceUnitCodeSchema,
});
const despatchFields = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema.nullable().default(null),
    flow: invoiceFlowSchema,
    family: z.enum(["E_DESPATCH", "PAPER"]).default("E_DESPATCH"),
    counterpartyId: uuidSchema,
    seriesId: uuidSchema.nullable().default(null),
    despatchNo: z
        .string()
        .trim()
        .min(1)
        .max(40)
        .nullable()
        .default(null)
        .describe("Yalnızca alış irsaliyesinde"),
    ettn: uuidSchema.nullable().default(null),
    issueDate: localDateSchema,
    issueTime: timeSchema.default("00:00"),
    shipmentAt: timestampSchema.nullable().default(null),
    vehiclePlate: optionalText(20),
    driverName: optionalText(200).describe("KVKK: kişisel veri"),
    driverTaxId: z
        .string()
        .regex(/^\d{11}$/, "TCKN 11 haneli olmalıdır")
        .nullable()
        .default(null)
        .describe("KVKK: kişisel veri"),
    carrierName: optionalText(300),
    carrierTaxId: taxIdSchema.nullable().default(null),
    deliveryAddress: optionalText(500),
    notes: optionalText(1000),
    deliveryId: uuidSchema.nullable().default(null),
    lines: z.array(despatchLineInputSchema).min(1, "En az bir satır gereklidir").max(200),
});
function despatchRules(value, context) {
    if (value.flow === "INCOMING" && value.despatchNo === null) {
        context.addIssue({
            code: "custom",
            path: ["despatchNo"],
            message: "Alış irsaliyesinde irsaliye numarası zorunludur",
        });
    }
    if (value.flow === "OUTGOING" && value.despatchNo) {
        context.addIssue({
            code: "custom",
            path: ["despatchNo"],
            message: "Sevk irsaliyesinin numarası kesinleştirmede üretilir",
        });
    }
    if (value.flow === "INCOMING" && value.seriesId) {
        context.addIssue({ code: "custom", path: ["seriesId"], message: "Alış irsaliyesinde seri seçilemez" });
    }
}
export const createDespatchSchema = despatchFields
    .extend({ issue: z.boolean().default(false) })
    .superRefine(despatchRules);
export const updateDespatchSchema = patchOf(despatchFields.omit({ companyId: true, flow: true })).extend({
    version: versionSchema,
});
export const issueDespatchSchema = z.object({ version: versionSchema });
export const cancelDespatchSchema = z.object({ reason: reasonSchema, version: versionSchema });
export const receiptAdviceSchema = z.object({
    status: z.enum(["ACCEPTED", "PARTIAL", "REJECTED"]),
    note: optionalText(500),
    version: versionSchema,
});
export const despatchSummarySchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema.nullable(),
    stationName: z.string().nullable(),
    flow: invoiceFlowSchema,
    family: z.enum(["E_DESPATCH", "PAPER"]),
    counterpartyId: uuidSchema,
    counterpartyName: z.string(),
    despatchNo: z.string().nullable(),
    issueDate: localDateSchema,
    vehiclePlate: z.string().nullable(),
    lineCount: z.number().int(),
    status: invoiceStatusSchema,
    edocumentStatus: edocumentStatusSchema,
    edocumentError: z.string().nullable(),
    receiptAdviceStatus: receiptAdviceStatusSchema,
    invoiceIds: z.array(uuidSchema),
    version: versionSchema,
});
export const despatchPageSchema = pageOf(despatchSummarySchema);
export const despatchSchema = despatchSummarySchema.extend({
    seriesId: uuidSchema.nullable(),
    ettn: uuidSchema,
    issueTime: z.string(),
    shipmentAt: timestampSchema.nullable(),
    driverName: z.string().nullable(),
    driverTaxId: z.string().nullable(),
    carrierName: z.string().nullable(),
    carrierTaxId: z.string().nullable(),
    deliveryAddress: z.string().nullable(),
    notes: z.string().nullable(),
    deliveryId: uuidSchema.nullable(),
    lines: z.array(despatchLineInputSchema.extend({
        id: uuidSchema,
        lineNo: z.number().int(),
        productCode: z.string().nullable(),
    })),
    invoices: z.array(z.object({ id: uuidSchema, invoiceNo: z.string().nullable() })),
    issuedAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
});
export const listDespatchesQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    flow: invoiceFlowSchema.optional(),
    status: invoiceStatusSchema.optional(),
    counterpartyId: uuidSchema.optional(),
    stationId: uuidSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
    unlinkedOnly: z
        .enum(["true", "false"])
        .transform((value) => value === "true")
        .optional()
        .describe("Yalnızca faturaya bağlanmamış irsaliyeler"),
    search: z.string().trim().max(100).optional(),
});
// ── POS'tan gün içi e-Arşiv talebi ───────────────────────────────────────────
export const EARCHIVE_REQUEST_STATUSES = ["PENDING", "INVOICED", "REJECTED"];
export const earchiveRequestStatusSchema = z.enum(EARCHIVE_REQUEST_STATUSES);
export const EARCHIVE_REQUEST_STATUS_LABELS = {
    PENDING: "Bekliyor",
    INVOICED: "Faturalandı",
    REJECTED: "Reddedildi",
};
export const createEarchiveRequestSchema = z
    .object({
    companyId: uuidSchema,
    stationId: uuidSchema,
    source: z.enum(["POS", "MANUAL"]).default("POS"),
    saleRef: z.string().trim().max(100).nullable().default(null),
    fiscalDeviceSerial: z.string().trim().min(1).max(40).nullable().default(null),
    zNo: z.number().int().positive().nullable().default(null),
    receiptNo: z.number().int().positive().nullable().default(null),
    receiptDate: localDateSchema,
    amount: positiveMoneySchema.describe("KDV dâhil fiş tutarı (kuruş)"),
    buyerTaxId: taxIdSchema.describe("KVKK: kişisel veri olabilir (TCKN)"),
    buyerName: z.string().trim().min(1).max(300).describe("KVKK: kişisel veri"),
    buyerTaxOffice: optionalText(100),
    buyerAddress: optionalText(500).describe("KVKK: kişisel veri"),
    buyerEmail: z.email("Geçerli bir e-posta adresi giriniz").max(254).nullable().default(null),
    vehiclePlate: optionalText(20),
})
    .superRefine((value, context) => {
    const parts = [value.fiscalDeviceSerial, value.zNo, value.receiptNo];
    if (parts.some((part) => part !== null) && parts.some((part) => part === null)) {
        context.addIssue({
            code: "custom",
            path: ["fiscalDeviceSerial"],
            message: "ÖKC seri no, Z no ve fiş no birlikte verilmelidir",
        });
    }
});
export const earchiveRequestSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    stationName: z.string().nullable(),
    source: z.enum(["POS", "MANUAL"]),
    saleRef: z.string().nullable(),
    fiscalDeviceSerial: z.string().nullable(),
    zNo: z.number().int().nullable(),
    receiptNo: z.number().int().nullable(),
    receiptDate: localDateSchema,
    amount: moneySchema,
    buyerTaxId: z.string(),
    buyerName: z.string(),
    buyerTaxOffice: z.string().nullable(),
    buyerAddress: z.string().nullable(),
    buyerEmail: z.string().nullable(),
    vehiclePlate: z.string().nullable(),
    status: earchiveRequestStatusSchema,
    invoiceId: uuidSchema.nullable(),
    rejectReason: z.string().nullable(),
    matchedCounterpartyId: uuidSchema
        .nullable()
        .describe("Alıcının VKN/TCKN'siyle eşleşen cari kartı; yoksa null (kart OTOMATİK açılmaz)"),
    createdAt: timestampSchema,
    version: versionSchema,
});
export const earchiveRequestPageSchema = pageOf(earchiveRequestSchema);
export const listEarchiveRequestsQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    status: earchiveRequestStatusSchema.optional(),
    stationId: uuidSchema.optional(),
});
export const rejectEarchiveRequestSchema = z.object({ reason: reasonSchema, version: versionSchema });
//# sourceMappingURL=invoicing.js.map